<template>
	<transition name="fade">
		<div class="garage-search__msg" v-if="errorMessage">
			<span>{{ errorMessage }}</span>
			<button class="btn btn-clear float-right"
				v-on:click="clearErrorMessage"><i class="fal fa-times"></i></button>
		</div>
	</transition>
	<div class="garage-search__search">
		<form>
			<div class="row">
				<div class="col-10">
					<div class="garage-search__input">
						<button class="btn btn-clear"
							v-on:click.prevent="locatorButtonPressed" type="button"
							data-search="geolocate">
							<i class="icon fal fa-location"></i>
						</button>

						<Autocomplete ref="autocomplete" size="100"
							v-bind:value="searchFieldInput" class="input input--clear"
							v-bind:placeholder="searchPlaceholder" data-search="text"
							v-bind:selectFirstOnEnter="true"
							v-on:place_changed="autocompletePlaceChange"
							v-on:keydown.enter="handleEnterKey" v-bind:options="{
								fields: ['formatted_address', 'geometry', 'address_components', 'types'],
								bounds: { north: 50, south: 38, east: 23, west: 6 },
								strictBounds: true
							}" />
					</div>
				</div>
				<div class="col-2">
					<button v-on:click.prevent="searchSubmit"
						class="btn btn-md btn-block btn-tertiary rounded-pill" type="submit"
						data-search="submit">
						<i class="icon fal fa-search"></i>
						<span class="btn-content text--md">{{ search }}</span>
					</button>
				</div>
			</div>
		</form>
	</div>
</template>

<script setup lang="ts">

import type { Position, CategoryType } from '@/types';
import { CategoryType as Category } from '@/types.d';
import { Autocomplete } from '@fawmi/vue-google-maps';
import { computed, onMounted, ref, type ComputedRef, type PropType } from 'vue';

//@ts-ignore
const search = Drupal.t('Search', {}, { 'context': 'Bip Garage App' });

const props = defineProps({
	activeCategory: {
		type: String as PropType<CategoryType>,
		default: Category.PARKING
	},
});
const browserLocationUsed = ref(false);
const searchFieldInput = ref('');
const emits = defineEmits<{
	(event: 'positionChanged', position: Position, meta: { searchValue?: string, countryCode?: string, types?: string[] }): void;
	(event: 'searchSubmit'): void,
}>();
const autocomplete = ref();
const errorMessage = ref('');

const handleEnterKey = function (event: KeyboardEvent) {
	const container = document.querySelector('.pac-container');
	if (container && window.getComputedStyle(container).display !== 'none') {
		event.preventDefault();
	} else {
		searchSubmit();
	}
};

const autocompletePlaceChange = function (place: google.maps.places.PlaceResult) {
	const searchValue = place.formatted_address!;
	const lat = place.geometry!.location!.lat();
	const lng = place.geometry!.location!.lng();
	const types = place.types;
	const countryCode = place.address_components?.find((value) => value.types.includes('country'));

	const positionData = { lat: lat, lng: lng };
	const metaData = { searchValue: searchValue, countryCode: countryCode?.short_name, types: types };

	emits('positionChanged', positionData, metaData);
};

onMounted(() => {
	autocomplete.value.$gmapApiPromiseLazy().then(() => {
		const initUrl = new URL(window.location.href);

		if (initUrl.searchParams.has('location')) {
			const location = initUrl.searchParams.get('location')!;
			let [lng, lat, ...loc] = location.split('|').reverse();
			searchFieldInput.value = loc.join(' ');
			autocompletePlaceChange({ formatted_address: loc.join('|'), geometry: { location: new google.maps.LatLng(parseFloat(lat), parseFloat(lng)) } })
		}
	});
})

const searchSubmit = function () {
	emits('searchSubmit');
}

const searchPlaceholder: ComputedRef<string> = computed(() => {
	switch (props.activeCategory) {
		case Category.PARKING:
			//@ts-ignore
			return Drupal.t('Where would you like to park?', {}, { 'context': 'Bip Garage App' });
		case Category.CHARGING:
			//@ts-ignore
			return Drupal.t('Where would you like to charge your e-car?', {}, { 'context': 'Bip Garage App' });
		case Category.SHARING:
			//@ts-ignore
			return Drupal.t('Where would you like to share an e-vehicle?', {}, { 'context': 'Bip Garage App' });
	}
});

const locatorButtonPressed = function () {
	navigator.geolocation.getCurrentPosition(
		function (position) {
			browserLocationUsed.value = true;
			//@ts-ignore
			searchFieldInput.value = Drupal.t('Your location', {}, { 'context': 'Bip Garage App' });
			errorMessage.value = '';

			//use "point_of_interest" to trigger higher zoom value
			emits('positionChanged', { lat: position.coords.latitude, lng: position.coords.longitude }, { searchValue: searchFieldInput.value, types: ['point_of_interest'] });
		},
		function (error) {
			switch (error.code) {
				case GeolocationPositionError.PERMISSION_DENIED:
					//@ts-ignore
					errorMessage.value = Drupal.t('To use your current location, please allow access to your location or use the search.', {}, { 'context': 'Bip Garage App' });
					break;
				case GeolocationPositionError.POSITION_UNAVAILABLE:
					//@ts-ignore
					errorMessage.value = Drupal.t('We are not able to detect your current location. Please enter an address via the search', {}, { 'context': 'Bip Garage App' });
					break;
				case GeolocationPositionError.TIMEOUT:
					//@ts-ignore
					errorMessage.value = Drupal.t('We are not able to detect your current location. Please try again', {}, { 'context': 'Bip Garage App' });
					break;
			}
		},
		{
			enableHighAccuracy: false //no need to be 100% precise
		}
	);
};

const clearErrorMessage = function () {
	errorMessage.value = '';
}
</script>
